import { useState } from 'react'
import Link from 'next/link'
import LanguageSwitcher from '../components/LanguageSwitcher'
import SEO from '../components/SEO'
import { LanguageProvider, useLanguage } from '../context/LanguageContext'
import ATTRIBUTION_COPY from '../locales/attribution'


// Recommended one-line paste — a single all-in-one SVG (contains the
// "Powered by" prefix + full logo lockup). Works in any host that
// accepts an <img>, including image-only surfaces (Facebook page cover,
// Squarespace image blocks, README badges) where HTML text won't render.
const BADGE_LIGHT_HTML = `<a href="https://creolecentric.com" target="_blank" rel="noopener">
  <img src="https://creolecentric.com/powered-by-creolecentric-light.svg"
       alt="Powered by CreoleCentric" height="32">
</a>`

const BADGE_DARK_HTML = `<a href="https://creolecentric.com" target="_blank" rel="noopener">
  <img src="https://creolecentric.com/powered-by-creolecentric-dark.svg"
       alt="Powered by CreoleCentric" height="32">
</a>`

// Fallback for developers who want to compose their own markup (HTML
// text + separate logo <img>). Inline styles so it renders identically
// regardless of the host page's CSS.
const BADGE_HTML = `<a href="https://creolecentric.com" target="_blank" rel="noopener"
   style="display:inline-flex;align-items:center;gap:6px;font:14px/1 -apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#6b7280;text-decoration:none">
  Powered by
  <img src="https://creolecentric.com/CreoleCentric-Logo-v7-inline-blue.svg"
       alt="CreoleCentric" style="height:20px;width:auto">
</a>`


export default function AttributionPage({ suggestedLanguage }) {
  return (
    <LanguageProvider initialSuggestion={suggestedLanguage}>
      <Attribution />
    </LanguageProvider>
  )
}


export async function getServerSideProps(context) {
  const INTERNAL_API = process.env.INTERNAL_API_URL || 'http://backend:8000'
  let suggestedLanguage = 'en'
  try {
    const cfCountry = context.req.headers['cf-ipcountry'] || ''
    const res = await fetch(`${INTERNAL_API}/v1/geo/`, {
      headers: { 'CF-IPCountry': cfCountry },
    })
    if (res.ok) {
      const data = await res.json()
      if (['en', 'ht', 'fr', 'es', 'pt-BR', 'zh-Hans'].includes(data.suggested_language)) {
        suggestedLanguage = data.suggested_language
      }
    }
  } catch (_) { /* geo hint is best-effort */ }
  return { props: { suggestedLanguage } }
}


function Attribution() {
  const [copiedKey, setCopiedKey] = useState(null)
  const { language } = useLanguage()
  const t = ATTRIBUTION_COPY[language] || ATTRIBUTION_COPY.en

  const copyAs = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedKey(key)
      setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 2000)
    } catch (_) {
      // Older browsers / iframe restrictions — user can still select
      // manually. Don't surface an error banner for a UX-nice-to-have.
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 relative">
      <SEO
        title={t.seo.title}
        description={t.seo.description}
        path="/attribution"
        lang={language}
      />
      <LanguageSwitcher />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3">
            {t.header.title}
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            {t.header.body}
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            {t.applies_to.h2}
          </h2>
          <ul className="text-gray-700 dark:text-gray-300 space-y-2 list-disc list-inside">
            <li>{t.applies_to.li_free.before}<strong>{t.applies_to.li_free.strong}</strong>{t.applies_to.li_free.after}</li>
            <li>{t.applies_to.li_grant.before}<strong>{t.applies_to.li_grant.strong}</strong>{t.applies_to.li_grant.after}</li>
            <li>{t.applies_to.li_mission.before}<strong>{t.applies_to.li_mission.strong}</strong>{t.applies_to.li_mission.after}</li>
          </ul>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
            {t.applies_to.footnote}
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            {t.where.h2}
          </h2>
          <ul className="text-gray-700 dark:text-gray-300 space-y-2 list-disc list-inside">
            <li>{t.where.li_web}</li>
            <li>{t.where.li_mobile}</li>
            <li>{t.where.li_social}</li>
          </ul>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
            {t.where.footnote.before}
            <code className="font-mono">{t.where.footnote.code}</code>
            {t.where.footnote.after}
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {t.badge.h2}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            {t.badge.subtitle}
          </p>

          {/* Light variant */}
          <div className="mb-6">
            <div className="flex items-baseline justify-between mb-2">
              <h3 className="font-semibold text-gray-900 dark:text-white">{t.badge.light_h3}</h3>
              <span className="text-xs text-gray-500 dark:text-gray-400">{t.badge.light_hint}</span>
            </div>
            <div style={{ background: '#F8F5EE' }} className="rounded-md p-6 flex justify-center border border-gray-200 dark:border-gray-700 mb-3">
              <img src="/powered-by-creolecentric-light.svg" alt={t.badge.light_alt} style={{ height: 56, width: 'auto' }} />
            </div>
            <div className="flex items-start justify-between gap-3 mb-2">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t.badge.paste_hint}
              </p>
              <button
                onClick={() => copyAs('light', BADGE_LIGHT_HTML)}
                className="text-sm px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors whitespace-nowrap"
              >
                {copiedKey === 'light' ? t.badge.copied : t.badge.copy}
              </button>
            </div>
            <pre className="bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-100 text-xs sm:text-sm p-4 rounded overflow-x-auto font-mono">
{BADGE_LIGHT_HTML}
            </pre>
          </div>

          {/* Dark variant */}
          <div>
            <div className="flex items-baseline justify-between mb-2">
              <h3 className="font-semibold text-gray-900 dark:text-white">{t.badge.dark_h3}</h3>
              <span className="text-xs text-gray-500 dark:text-gray-400">{t.badge.dark_hint}</span>
            </div>
            <div style={{ background: '#0F1B3D' }} className="rounded-md p-6 flex justify-center border border-gray-200 dark:border-gray-700 mb-3">
              <img src="/powered-by-creolecentric-dark.svg" alt={t.badge.dark_alt} style={{ height: 56, width: 'auto' }} />
            </div>
            <div className="flex items-start justify-between gap-3 mb-2">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t.badge.paste_hint}
              </p>
              <button
                onClick={() => copyAs('dark', BADGE_DARK_HTML)}
                className="text-sm px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors whitespace-nowrap"
              >
                {copiedKey === 'dark' ? t.badge.copied : t.badge.copy}
              </button>
            </div>
            <pre className="bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-100 text-xs sm:text-sm p-4 rounded overflow-x-auto font-mono">
{BADGE_DARK_HTML}
            </pre>
          </div>
        </div>

        <details className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8">
          <summary className="cursor-pointer text-lg font-semibold text-gray-900 dark:text-white">
            {t.advanced.summary}
          </summary>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-4 mb-4">
            {t.advanced.body}
          </p>

          <div className="flex items-start justify-between gap-3 mb-2">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              {t.advanced.snippet_label}
            </p>
            <button
              onClick={() => copyAs('html', BADGE_HTML)}
              className="text-sm px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors whitespace-nowrap"
            >
              {copiedKey === 'html' ? t.badge.copied : t.badge.copy}
            </button>
          </div>
          <pre className="bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-100 text-xs sm:text-sm p-4 rounded overflow-x-auto font-mono mb-6">
{BADGE_HTML}
          </pre>

          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            {t.advanced.logo_label}
          </p>
          <a
            href="/CreoleCentric-Logo-v7-inline-blue.svg"
            download="CreoleCentric-Logo-inline.svg"
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            {t.advanced.download_svg}
          </a>
        </details>

        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            {t.remove.h2}
          </h2>
          <p className="text-gray-700 dark:text-gray-300 mb-3">
            {t.remove.body}
          </p>
          <div className="flex gap-3 flex-wrap">
            <Link
              href="/subscription"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-semibold"
            >
              {t.remove.cta_upgrade}
            </Link>
            <Link
              href="/for-community"
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded"
            >
              {t.remove.cta_apply}
            </Link>
          </div>
        </div>

      </div>
    </div>
  )
}
